import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { LuGift, LuCopy, LuCheck, LuUsers, LuBadgePercent } from 'react-icons/lu';
import { FaWhatsapp } from 'react-icons/fa';

import { Modal, ModalTitle, ModalBody, ModalFooter } from './Modal';

const WHATSAPP_NUMBER = '552132798800';

const buildMensagemIndicacao = (codigo) =>
  `Olá! Quero te indicar o plano da Santa Casa Copacabana.

Use o meu código de indicação: ${codigo}

Fale com a equipe pelo WhatsApp:
https://wa.me/${WHATSAPP_NUMBER}.`;

const buildMensagemDuvidasPrograma = (codigo) =>
  `Olá! Gostaria de entender melhor como funciona o programa de indicação da Santa Casa.${
    codigo ? ` Meu código é ${codigo}.` : ''
  }`;

const Card = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
  padding: 28px 32px;
  border-radius: 16px;
  background: linear-gradient(135deg, #1e3a8a 0%, rgb(19, 85, 154) 55%, #1e40af 100%);
  color: #ffffff;
  box-shadow: 0 8px 25px rgba(30, 58, 138, 0.25);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: 20%;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(219, 234, 254, 0.12);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 16px 14px;
    gap: 16px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #ffffff;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.5;
  max-width: 420px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    align-items: stretch;
  }
`;

const CopyButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 22px;
  border: none;
  border-radius: 12px;
  background: #ffffff;
  color: #1e3a8a;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #dbeafe;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      background: #ffffff;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CodeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
  }
`;

const CodeLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
`;

const CodeValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$single ? '1fr' : '1fr 1.4fr'};
  gap: 12px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: #ffffff;
`;

const StatLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.35;
`;

const RulesLink = styled.button`
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  color: #bfdbfe;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #ffffff;
  }
`;

const RulesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    color: #475569;
    font-size: 14px;
    line-height: 1.55;
  }

  strong {
    color: #1e3a8a;
    font-weight: 700;
  }
`;

const FooterActions = styled(ModalFooter)`
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const ConfirmButton = styled.button`
  background: linear-gradient(135deg, #1e3a8a 0%, rgb(19, 85, 154) 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #25d366;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    color: #ffffff;
    text-decoration: none;
  }
`;

const ReferralInviteCard = ({ codigoIndicacao, totalIndicados = 0 }) => {
  const [copied, setCopied] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const indicadosLabel =
    totalIndicados === 1 ? 'pessoa indicada' : 'pessoas indicadas';

  const whatsappDuvidasHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    buildMensagemDuvidasPrograma(codigoIndicacao)
  )}`;

  const handleCopy = async () => {
    if (!codigoIndicacao) {
      toast.error('Código de indicação indisponível para este contrato.');
      return;
    }

    const mensagem = buildMensagemIndicacao(codigoIndicacao);

    try {
      await navigator.clipboard.writeText(mensagem);
      setCopied(true);
      toast.success('Código de indicação copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar. Tente novamente.');
    }
  };

  return (
    <>
      <Card
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <TopRow>
          <Content>
            <IconWrap>
              <LuGift />
            </IconWrap>
            <TextBlock>
              <Title>Indique a Santa Casa</Title>
              <Subtitle>
                Compartilhe seu código e ajude alguém a conhecer o plano.
              </Subtitle>
            </TextBlock>
          </Content>

          <Actions>
            <CodeBlock>
              <CodeLabel>Seu código</CodeLabel>
              <CodeValue>{codigoIndicacao ?? 'Indisponível'}</CodeValue>
            </CodeBlock>
            <CopyButton
              type="button"
              onClick={handleCopy}
              disabled={!codigoIndicacao}
              whileTap={{ scale: 0.97 }}
              aria-label="Copiar código de indicação"
            >
              {copied ? <LuCheck /> : <LuCopy />}
              {copied ? 'Copiado!' : 'Copiar código de indicação'}
            </CopyButton>
          </Actions>
        </TopRow>

        <StatsRow $single={totalIndicados === 0}>
          {totalIndicados > 0 ? (
            <StatBox>
              <StatIcon>
                <LuUsers />
              </StatIcon>
              <StatText>
                <StatValue>{totalIndicados}</StatValue>
                <StatLabel>{indicadosLabel}</StatLabel>
              </StatText>
            </StatBox>
          ) : null}

          <StatBox>
            <StatIcon>
              <LuBadgePercent />
            </StatIcon>
            <StatText>
              <StatValue>1 mensalidade grátis</StatValue>
              <StatLabel>para cada pessoa que você indicar</StatLabel>
              <RulesLink type="button" onClick={() => setIsRulesOpen(true)}>
                Clique aqui e veja as regras
              </RulesLink>
            </StatText>
          </StatBox>
        </StatsRow>
      </Card>

      <Modal isOpen={isRulesOpen} onClose={() => setIsRulesOpen(false)}>
        <ModalTitle>Regras do programa de indicação</ModalTitle>
        <ModalBody>
          <RulesList>
            <li>
              <strong>Benefício:</strong> para cada pessoa indicada, você pode
              ganhar 1 mensalidade grátis do seu plano.
            </li>
            <li>
              <strong>Quando vale:</strong> o benefício só é liberado depois que
              o indicado contratar o plano e realizar o pagamento da primeira
              mensalidade.
            </li>
            <li>
              <strong>Como indicar:</strong> compartilhe seu código de indicação.
              O indicado precisa informar esse código no momento da contratação.
            </li>
            <li>
              <strong>Quem pode indicar:</strong> todos os associados poderão
              realizar indicações.
            </li>
            <li>
              <strong>Tipo de indicação:</strong> CNPJs só podem indicar clientes pessoa jurídica, e pessoas
              físicas só podem indicar clientes pessoa física.
            </li>
            <li>
              <strong>Limitações:</strong> autoindicação e indicação de
              dependentes do próprio contrato não são válidas.
            </li>
            <li>
              <strong>Aplicação do benefício:</strong> o abono será aplicado na
              mensalidade mais antiga em aberto. Caso não exista mensalidade em
              aberto, o benefício será aplicado no próximo vencimento.
            </li>
            <li>
              <strong>Alterações:</strong> este programa de indicação pode ser
              alterado ou cancelado a qualquer momento, sem aviso prévio.
            </li>
            <li>
              Em caso de dúvida, fale com a equipe da Santa Casa pelo WhatsApp
              ou pelos canais oficiais de atendimento.
            </li>
          </RulesList>
        </ModalBody>
        <FooterActions>
          <WhatsAppButton
            href={whatsappDuvidasHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp size={18} />
            Falar no WhatsApp
          </WhatsAppButton>
          <ConfirmButton type="button" onClick={() => setIsRulesOpen(false)}>
            Entendi
          </ConfirmButton>
        </FooterActions>
      </Modal>
    </>
  );
};

export default ReferralInviteCard;
